import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const ROLE_OPTIONS = [
  { code: 'role-acct-mgmt', label: 'Account Management' },
  { code: 'role-key-mgmt', label: 'Key Management' },
];

const Invites = ({ isOrg, canManageAccount }) => {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [confirmCancelInvite, setConfirmCancelInvite] = useState(null);

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await api.get('/acct-mgmt/v1/account/invites');
      setInvites(response.data || []);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load invites.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOrg || !canManageAccount) return;
    fetchInvites();
  }, [isOrg, canManageAccount]);

  if (!isOrg) {
    return (
      <div>
        <div className="mb-3">
          <h2 className="h4 fw-bold mb-1">Invites</h2>
          <p className="text-muted mb-0">Invite users and manage pending invites.</p>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-muted mb-0">Individual accounts cannot invite users.</p>
          </div>
        </div>
      </div>
    );
  }
  if (!canManageAccount) {
    return (
      <div>
        <div className="mb-3">
          <h2 className="h4 fw-bold mb-1">Invites</h2>
          <p className="text-muted mb-0">Invite users and manage pending invites.</p>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-muted mb-0">You do not have permission to invite users.</p>
          </div>
        </div>
      </div>
    );
  }

  const toggleRole = (role) => {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (roles.length === 0) {
      setStatus({ type: 'danger', message: 'Select at least one role.' });
      return;
    }
    try {
      await api.post('/acct-mgmt/v1/account/invite', { email, roles });
      setStatus({ type: 'success', message: 'Invite sent.' });
      setEmail('');
      setRoles([]);
      fetchInvites();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send invite.' });
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api.delete(`/acct-mgmt/v1/account/invite/${inviteId}`);
      setStatus({ type: 'success', message: 'Invite canceled.' });
      fetchInvites();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to cancel invite.' });
    }
  };

  const resendInvite = async (inviteId) => {
    try {
      await api.post(`/acct-mgmt/v1/account/invite/${inviteId}/resend`);
      setStatus({ type: 'success', message: 'Invite resent.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to resend invite.' });
    }
  };

  const pendingInvites = invites.filter((invite) => invite.status === 'Pending');

  return (
    <div>
      <div className="mb-3">
        <h2 className="h4 fw-bold mb-1">Invites</h2>
        <p className="text-muted mb-0">Invite users and manage pending invites.</p>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          {status.message && (
            <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
          )}
          <h5 className="card-title">Invite a User</h5>
          <form onSubmit={handleInvite}>
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Roles</label>
                <div className="d-flex flex-wrap gap-3">
                  {ROLE_OPTIONS.map((role) => (
                    <div className="form-check form-switch finbase-toggle" key={role.code}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`invite-role-${role.code}`}
                        checked={roles.includes(role.code)}
                        onChange={() => toggleRole(role.code)}
                      />
                      <label className="form-check-label" htmlFor={`invite-role-${role.code}`}>
                        {role.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button className="btn btn-primary mt-3" type="submit">Send Invite</button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-2">
            <h5 className="card-title">Pending Invites</h5>
          </div>
          <div className="mb-3">
            <a className="btn btn-link p-0" href="/account/invites/history">
              View Invite History
            </a>
          </div>
          {loading ? (
            <div className="text-muted">Loading invites...</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((invite) => (
                    <tr key={invite.id}>
                      <td>{invite.email}</td>
                      <td>
                        {(invite.roles || []).length === 0 ? (
                          <span className="text-muted">None</span>
                        ) : (
                          (invite.roles || []).map((role) => (
                            <span key={role} className="badge bg-light text-dark border me-1">
                              {ROLE_OPTIONS.find((r) => r.code === role)?.label || role}
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          invite.status === 'Pending'
                            ? 'bg-warning text-dark'
                            : invite.status === 'Accepted'
                              ? 'bg-success'
                              : invite.status === 'Rejected'
                                ? 'bg-secondary'
                                : 'bg-light text-dark'
                        }`}>
                          {invite.status}
                        </span>
                      </td>
                      <td>{new Date(invite.expires_at).toLocaleDateString()}</td>
                    <td className="text-end">
                      {invite.status === 'Pending' && (
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => resendInvite(invite.id)}
                        >
                          Resend
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setConfirmCancelInvite(invite)}
                        disabled={invite.status !== 'Pending'}
                      >
                        Cancel
                      </button>
                      </td>
                    </tr>
                  ))}
                  {pendingInvites.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-muted text-center">No Pending Invites.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {confirmCancelInvite && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Cancel invite?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setConfirmCancelInvite(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will cancel the invite for <strong>{confirmCancelInvite.email}</strong>.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setConfirmCancelInvite(null)}
                  >
                    Keep invite
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      const targetInvite = confirmCancelInvite;
                      setConfirmCancelInvite(null);
                      await cancelInvite(targetInvite.id);
                    }}
                  >
                    Yes, Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Invites;
